import React, { useMemo } from "react";
import { useTitle } from "../../../../../hooks/useTitle";
import { withAuth } from "../../../../../components/HOC/withAuth";
import CardContainer from "../../../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../../../components/BaseUI/Table";
import { SectionSpinner, SectionEmpty } from "../../shared/utils";
import ProductionReportShell from "../ProductionReportShell";
import GroupByChips from "../GroupByChips";
import { useMachineProductionReport } from "../useMachineProductionReport";
import "../../Reports.scss";

const fmtInt = (n) => Math.round(Number(n) || 0).toLocaleString("tr-TR");
const fmtDateKey = (dk) => {
  const [y, m, d] = String(dk).split("-");
  return d && m && y ? `${d}.${m}` : dk;
};

const ShareBar = ({ pct }) => (
  <div className="d-flex align-items-center gap-2" style={{ minWidth: 120 }}>
    <div
      style={{
        flex: 1,
        height: 6,
        background: "#e2e8f0",
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${Math.min(100, pct)}%`,
          height: "100%",
          background: "#0f766e",
          borderRadius: 3,
        }}
      />
    </div>
    <span style={{ fontSize: 12, minWidth: 36, textAlign: "right" }}>
      {pct}%
    </span>
  </div>
);

const ProductionByMachineDistribution = () => {
  useTitle("Rovion MES - Günlük Üretim Tablosu");
  const {
    filters,
    machines,
    shifts,
    loading,
    derived,
    groupBy,
    setGroupBy,
    fetchData,
    toasts,
    dismissToast,
  } = useMachineProductionReport();

  const tableModel = useMemo(() => {
    if (!derived) return null;
    const { dateKeys, seriesByMachine, machineTotals, totalPerDate } = derived;
    const grandTotal = machineTotals.reduce((s, m) => s + m.total, 0);
    if (!grandTotal) return null;

    const seriesMap = Object.fromEntries(
      seriesByMachine.map((s) => [s.name, s.data]),
    );

    const rows = machineTotals.map(({ name, total }) => ({
      name,
      values: dateKeys.map((_, i) => seriesMap[name]?.[i] || 0),
      total,
      share: Math.round((total / grandTotal) * 1000) / 10,
    }));

    return {
      dateKeys,
      dateLabels: dateKeys.map(fmtDateKey),
      rows,
      periodTotals: totalPerDate,
      grandTotal,
    };
  }, [derived]);

  const content = () => {
    if (loading) return <SectionSpinner />;
    if (!tableModel)
      return <SectionEmpty msg="Dağılım için veri bulunamadı." />;

    const { dateLabels, rows, periodTotals, grandTotal } = tableModel;

    const columns = [
      { name: "Makine", id: 1, sortable: true },
      ...dateLabels.map((label, id) => ({
        name: label,
        id: id + 2,
        sortable: true,
      })),
      { name: "Toplam", id: 900, sortable: true },
      { name: "Pay %", id: 901, sortable: false },
    ];

    return (
      <>
        {/* <GroupByChips groupBy={groupBy} onChange={setGroupBy} /> */}
        <CardContainer title="Makine Bazlı Üretim Dağılımı">
          <div style={{ overflowX: "auto" }}>
            <Table
              columns={columns}
              search
              defaultPageSize={25}
              hideFooter={rows.length <= 25}
              footer={
                <tr className="rp-dist-total-row">
                  <td>
                    <span className="rp-dist-total-label">TOPLAM</span>
                  </td>
                  {periodTotals.map((qty, i) => (
                    <td key={`total-${i}`}>{fmtInt(qty)}</td>
                  ))}
                  <td>{fmtInt(grandTotal)}</td>
                  <td>
                    <span className="rp-dist-total-pct">100%</span>
                  </td>
                </tr>
              }
            >
              {rows.map((row) => (
                <Tr key={row.name} data={row}>
                  <Td>
                    <strong>{row.name}</strong>
                  </Td>
                  {row.values.map((qty, i) => (
                    <Td key={`${row.name}-${i}`}>
                      {qty > 0 ? (
                        <strong>{fmtInt(qty)}</strong>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </Td>
                  ))}
                  <Td>
                    <strong>{fmtInt(row.total)}</strong>
                  </Td>
                  <Td>
                    <ShareBar pct={row.share} />
                  </Td>
                </Tr>
              ))}
            </Table>
          </div>
        </CardContainer>
      </>
    );
  };

  return (
    <ProductionReportShell
      title="Günlük Üretim Tablosu"
      filters={filters}
      machines={machines}
      shifts={shifts}
      onApply={fetchData}
      toasts={toasts}
      dismissToast={dismissToast}
    >
      {content()}
    </ProductionReportShell>
  );
};

export default withAuth(ProductionByMachineDistribution);
